import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Save, Loader2, History, RotateCcw, Eye } from 'lucide-react';

export const IstruzioniAI = ({ isAdmin }) => {
  const [testo, setTesto] = useState('');
  const [attivo, setAttivo] = useState(null);
  const [versioni, setVersioni] = useState([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const r = await api.get('/ai-admin/prompt');
      setAttivo(r.data.attivo);
      setTesto(r.data.attivo?.testo || '');
      setVersioni(r.data.versioni || []);
    } catch { toast.error('Errore caricamento istruzioni'); }
  };
  useEffect(() => { load(); }, []);

  const salva = async () => {
    setSaving(true);
    try {
      await api.post('/ai-admin/prompt', { testo });
      toast.success('Nuova versione salvata: da ora l\'AI usa queste istruzioni');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const ripristina = async (vid) => {
    try {
      await api.post(`/ai-admin/prompt/ripristina/${vid}`);
      toast.success('Versione ripristinata');
      setPreview(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore ripristino'); }
  };

  const vedi = async (vid) => {
    try {
      const r = await api.get(`/ai-admin/prompt/versione/${vid}`);
      setPreview(r.data);
    } catch { toast.error('Errore'); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="istruzioni-ai">
      <Card className="lg:col-span-2">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><FileText size={15} /> Istruzioni dell'assistente (system prompt)</CardTitle>
          <p className="text-[11px] text-slate-400">Ogni conversazione usa sempre l'ultima versione salvata. Versione attiva: <span className="font-bold">v{attivo?.versione || '-'}</span> ({attivo?.created_at?.slice(0, 16).replace('T', ' ') || ''})</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea rows={26} value={testo} onChange={e => setTesto(e.target.value)}
            className="text-xs font-mono leading-relaxed" disabled={!isAdmin} data-testid="prompt-textarea" />
          {isAdmin && (
            <Button onClick={salva} disabled={saving || !testo.trim()} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="prompt-save-btn">
              {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />} Salva nuova versione
            </Button>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><History size={15} /> Storico versioni</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 max-h-[560px] overflow-y-auto">
          {versioni.map(v => (
            <div key={v.id} className={`border rounded-lg p-2.5 text-xs flex items-center justify-between gap-2 ${v.attiva ? 'border-violet-300 bg-violet-50/50' : 'bg-slate-50/60'}`} data-testid={`prompt-versione-${v.versione}`}>
              <div>
                <p className="font-semibold">v{v.versione} {v.attiva && <Badge className="ml-1 bg-violet-100 text-violet-700 border-0 text-[9px]">ATTIVA</Badge>}</p>
                <p className="text-slate-400">{v.created_at?.slice(0, 16).replace('T', ' ')} — {v.autore}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => vedi(v.id)} title="Vedi"><Eye size={13} /></Button>
                {isAdmin && !v.attiva && (
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-violet-600" onClick={() => ripristina(v.id)} title="Ripristina" data-testid={`prompt-ripristina-${v.versione}`}><RotateCcw size={13} /></Button>
                )}
              </div>
            </div>
          ))}
          {preview && (
            <div className="border rounded-lg p-3 bg-white text-xs space-y-2">
              <p className="font-semibold">Anteprima v{preview.versione}</p>
              <pre className="whitespace-pre-wrap max-h-64 overflow-y-auto text-[10px] text-slate-600">{preview.testo}</pre>
              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setPreview(null)}>Chiudi</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
